/** Formatting helpers. Mirrors the behaviour of app/formatters.py. */

import type { Period } from "@/api/types";

const PERIOD_LABELS: Record<Period, string> = {
  today: "Hoy",
  week: "Semana",
  month: "Mes",
  month_current: "Mes actual",
  month_previous: "Mes pasado",
  quarter: "Trimestre",
  all: "Todo",
};

/** Spanish label for a Period value. */
export function periodLabel(p: Period): string {
  return PERIOD_LABELS[p] ?? String(p);
}

const MONTHS_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/** Compact number formatter: 1234 → "1.2K", 1_500_000 → "1.5M". */
export function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "0";
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}G`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
}

/** Percentage with a fixed "%" suffix. Clamps to 0 if input is nullish. */
export function pct(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "0%";
  return `${n.toFixed(digits)}%`;
}

/** "2025-04-29" → "29 de abril de 2025". Accepts ISO strings or Date. */
export function dateSpanish(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.getUTCDate();
  const month = MONTHS_ES[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  return `${day} de ${month} de ${year}`;
}

/** Short week label for chart axes: "2026-04-13" → "Sem 13 abr". */
export function weekShort(value: string): string {
  const parts = value.split("-");
  const m = parts[1];
  const dd = parts[2];
  if (!m || !dd) return value;
  const idx = Number.parseInt(m, 10) - 1;
  const label = MONTHS_ES[idx];
  if (!label) return value;
  return `Sem ${Number.parseInt(dd, 10)} ${label.slice(0, 3)}`;
}

/** Short day label for chart axes: "2026-04-15" → "15 abr". */
export function dayShort(value: string): string {
  const parts = value.split("-");
  const m = parts[1];
  const dd = parts[2];
  if (!m || !dd) return value;
  const idx = Number.parseInt(m, 10) - 1;
  const label = MONTHS_ES[idx];
  if (!label) return value;
  return `${Number.parseInt(dd, 10)} ${label.slice(0, 3)}`;
}

/** Short month label for chart axes: "2025-04" → "abr 2025". */
export function monthShort(value: string): string {
  const parts = value.split("-");
  const y = parts[0];
  const m = parts[1];
  if (!y || !m) return value;
  const idx = Number.parseInt(m, 10) - 1;
  const label = MONTHS_ES[idx];
  if (!label) return value;
  return `${label.slice(0, 3)} ${y}`;
}
